import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { SetRequired } from 'type-fest';

import * as PackageInventoryStore from '../../../redux/packageInventory';
import FilterCard from '../../generic/Filter/FilterCard';
import QualityRequired from '../../generic/Gates/QualityRequired';
import ProjectLabels from '../../projects/dashboardV2/ProjectFilters/ProjectLabels';
import useFetchPackageVersionProjects from '../hooks/useFetchPackageVersionProjects';
import type { AnyFilter } from '../../../../shared/organizationPackages/filters';
import type { MultiSelectFilterOption } from '../../generic/Filter/MultiSelectFilter';
import type { PackageInventoryParams } from '../PackageInventoryContainer';

import PackageBlockType from './PackageBlockType';
import PackageInventoryFiltersPackageDepth from './PackageDepth';
import ProjectNameFilters from './ProjectNameFilters';
import PackageInventoryFiltersProjectSource from './ProjectSource';
import PackageInventoryFiltersProjectVisibility from './ProjectVisibility';

type Props = {
  filters: AnyFilter;
  setFilters: (filters: AnyFilter) => void;
};

/**
 * PackageInventoryFiltersProject has filters related to searching
 * by project name, import method, visibility, or project label.
 */
const PackageInventoryFiltersProject: VoidFunctionComponent<Props> = ({
  filters,
  setFilters,
}) => (
  <FilterCard id="package-inventory-project-filters" title="Project">
    <ProjectNameFilters projectName={filters.projectName ?? ''} setFilters={setFilters} />
    <PackageInventoryFiltersProjectSource sources={filters.sources ?? []} setFilters={setFilters} />
    <PackageInventoryFiltersProjectVisibility visibility={filters.visibility ?? []} setFilters={setFilters} />
    <ProjectLabels
      filterLabels={filters.labels ?? []}
      onChange={(items: MultiSelectFilterOption[]) => setFilters({ labels: items.map(item => item.value) })}
      testId="package-inventory-filters--project-labels"
    />
    <PackageInventoryFiltersPackageDepth depths={filters.depth ?? []} setFilters={setFilters} />
    <QualityRequired>
      <PackageBlockType blockTypes={filters.blockTypes ?? []} setFilters={setFilters} />
    </QualityRequired>
  </FilterCard>
);

/**
 * Stateful container for the project filters as they are on the main /packages page.
 */
export const PackageInventoryFiltersProjectPackagesContainer: VoidFunctionComponent<Props> = ({ filters, setFilters }) => {
  return (
    <PackageInventoryFiltersProject
      filters={filters}
      setFilters={setFilters}
    />
  );
};

type PackageInventoryFiltersProjectVersionContainerProps = {
  params: SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>;
};

/**
 * Stateful container for the project filters as they are on the /packages/:locator/:version page.
 */
export const PackageInventoryFiltersProjectVersionContainer: VoidFunctionComponent<PackageInventoryFiltersProjectVersionContainerProps> = ({ // eslint-disable-line max-len -- It's fine.
  params,
}) => {
  const labels = useSelector(PackageInventoryStore.selectors.getFiltersProjectLabels);
  const projectName = useSelector(PackageInventoryStore.selectors.getFiltersProjectName);
  const depth = useSelector(PackageInventoryStore.selectors.getDepthFilters);
  const blockTypes = useSelector(PackageInventoryStore.selectors.getBlockTypeFilters);
  const sources = useSelector(PackageInventoryStore.selectors.getFiltersProjectSources);
  const visibility = useSelector(PackageInventoryStore.selectors.getFiltersProjectVisibility);
  const setFilters = useFetchPackageVersionProjects(
    PackageInventoryStore.actions.setFilters,
    params.packageLocator,
    params.version,
  );
  return (
    <PackageInventoryFiltersProject
      filters={{ labels, projectName, depth, blockTypes, sources, visibility }}
      setFilters={setFilters}
    />
  );
};
